/**
 * Search Query Parser — FR-SEARCH-2
 *
 * Parses boolean search operators for in-visualizer search.
 * Supports: +AND, -NOT, "exact phrase", OR (|), /regex/, escape (\+, \-, \")
 *
 * Default (no operators) = plain substring search (backward compatible).
 */

/* ── Types ─────────────────────────────────────────────────────────────────── */

export interface SearchTerm {
  type: 'include' | 'exclude' | 'exact' | 'regex';
  value: string;
  operator: 'AND' | 'OR';
  /** Pre-compiled regex for type === 'regex'. null if invalid. */
  compiledRegex?: RegExp | null;
}

export interface SearchQuery {
  terms: SearchTerm[];
  caseSensitive: boolean;
  /** true when the raw input contains no operator syntax (plain substring mode) */
  isPlainText: boolean;
}

export interface HighlightRange {
  start: number;
  end: number;
}

/* ── Parser ────────────────────────────────────────────────────────────────── */

const OPERATOR_PATTERN = /(?:^|[\s])[\+\-](?=\S)|"[^"]*"|\/[^/]+\/|(?:\s\|\s)|\\\+|\\-|\\"|\\\/|\\\\/;

/** Returns true when the raw query string contains any operator syntax. */
export function hasOperators(raw: string): boolean {
  return detectOperators(raw.trim());
}

function detectOperators(trimmed: string): boolean {
  if (!trimmed) return false;
  return OPERATOR_PATTERN.test(trimmed);
}

/**
 * Tokenise a raw query string into structured search terms.
 *
 * Syntax handled (in scanning order):
 *  1. Escaped operators: \+  \-  \"  \/  \|  \\
 *  2. Exact phrase:      "some phrase"
 *  3. Regex:             /pattern/
 *  4. OR pipe:           term1 | term2
 *  5. AND prefix:        +term
 *  6. NOT prefix:        -term
 *  7. Plain word:        term  (treated as include/AND)
 */
export function parseSearchQuery(raw: string, caseSensitive = false): SearchQuery {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { terms: [], caseSensitive, isPlainText: true };
  }

  // Detect whether the query uses any operator syntax
  const operators = detectOperators(trimmed);

  if (!operators) {
    // Plain substring — backward-compatible path
    return {
      terms: [{ type: 'include', value: trimmed, operator: 'AND' }],
      caseSensitive,
      isPlainText: true,
    };
  }

  const terms: SearchTerm[] = [];
  let i = 0;
  let nextOperator: 'AND' | 'OR' = 'AND';

  while (i < trimmed.length) {
    // Skip whitespace
    if (trimmed[i] === ' ') { i++; continue; }

    // OR pipe: `|` — retroactively mark the previous term as OR too
    if (trimmed[i] === '|') {
      nextOperator = 'OR';
      // The term before the pipe is also part of the OR group
      if (terms.length > 0 && terms[terms.length - 1].operator === 'AND' && terms[terms.length - 1].type !== 'exclude') {
        terms[terms.length - 1].operator = 'OR';
      }
      i++;
      continue;
    }

    // Escaped operator: \+ \- \" \/ \| \\
    if (trimmed[i] === '\\' && i + 1 < trimmed.length && '+-"/|\\'.includes(trimmed[i + 1])) {
      const word = consumeWord(trimmed, i + 1);
      terms.push({ type: 'include', value: word.value, operator: nextOperator });
      nextOperator = 'AND';
      i = word.end;
      continue;
    }

    // Exact phrase: "..."
    if (trimmed[i] === '"') {
      const close = trimmed.indexOf('"', i + 1);
      if (close !== -1) {
        const phrase = trimmed.substring(i + 1, close);
        if (phrase.length > 0) {
          terms.push({ type: 'exact', value: phrase, operator: nextOperator });
        }
        nextOperator = 'AND';
        i = close + 1;
        continue;
      }
      // No closing quote — treat as literal
    }

    // Regex: /pattern/
    if (trimmed[i] === '/') {
      const close = trimmed.indexOf('/', i + 1);
      if (close !== -1) {
        const pattern = trimmed.substring(i + 1, close);
        let compiled: RegExp | null = null;
        try {
          compiled = new RegExp(pattern, caseSensitive ? '' : 'i');
        } catch {
          compiled = null; // invalid regex — matchesSearchQuery will return false
        }
        terms.push({
          type: 'regex',
          value: pattern,
          operator: nextOperator,
          compiledRegex: compiled,
        });
        nextOperator = 'AND';
        i = close + 1;
        continue;
      }
    }

    // AND prefix: +term
    if (trimmed[i] === '+' && i + 1 < trimmed.length && trimmed[i + 1] !== ' ') {
      const word = consumeWord(trimmed, i + 1);
      terms.push({ type: 'include', value: word.value, operator: 'AND' });
      nextOperator = 'AND';
      i = word.end;
      continue;
    }

    // NOT prefix: -term
    if (trimmed[i] === '-' && i + 1 < trimmed.length && trimmed[i + 1] !== ' ') {
      const word = consumeWord(trimmed, i + 1);
      terms.push({ type: 'exclude', value: word.value, operator: 'AND' });
      nextOperator = 'AND';
      i = word.end;
      continue;
    }

    // Plain word
    const word = consumeWord(trimmed, i);
    if (word.value.length > 0) {
      terms.push({ type: 'include', value: word.value, operator: nextOperator });
      nextOperator = 'AND';
    }
    i = word.end;
  }

  return { terms, caseSensitive, isPlainText: false };
}

/** Consume a contiguous word (non-space, not a pipe/quote/slash delimiter). */
function consumeWord(s: string, start: number): { value: string; end: number } {
  let end = start;
  let value = '';
  while (end < s.length && s[end] !== ' ' && s[end] !== '|') {
    // Handle escape sequences inside words
    if (s[end] === '\\' && end + 1 < s.length && '+-"/|\\'.includes(s[end + 1])) {
      value += s[end + 1];
      end += 2;
      continue;
    }
    value += s[end];
    end++;
  }
  return { value, end };
}

/* ── Matcher ───────────────────────────────────────────────────────────────── */

/**
 * Test whether `text` satisfies a parsed search query.
 *
 * Logic:
 *  - Group terms by their operator into AND-groups and OR-groups.
 *  - All AND terms must match; at least one OR term (if any) must match.
 *  - Exclude terms are always AND (all excludes must not match).
 */
export function matchesSearchQuery(text: string, query: SearchQuery): boolean {
  if (query.terms.length === 0) return true;

  const haystack = query.caseSensitive ? text : text.toLowerCase();

  // Plain text fast-path
  if (query.isPlainText) {
    const needle = query.caseSensitive ? query.terms[0].value : query.terms[0].value.toLowerCase();
    return haystack.includes(needle);
  }

  // Partition into AND-terms, OR-terms, and exclude-terms
  const andTerms: SearchTerm[] = [];
  const orTerms: SearchTerm[] = [];
  const excludeTerms: SearchTerm[] = [];

  for (const term of query.terms) {
    if (term.type === 'exclude') {
      excludeTerms.push(term);
    } else if (term.operator === 'OR') {
      orTerms.push(term);
    } else {
      andTerms.push(term);
    }
  }

  // All excludes must NOT match
  for (const term of excludeTerms) {
    if (termMatches(haystack, term, query.caseSensitive)) return false;
  }

  // All AND terms must match
  for (const term of andTerms) {
    if (!termMatches(haystack, term, query.caseSensitive)) return false;
  }

  // If there are OR terms, at least one must match
  if (orTerms.length > 0) {
    const anyOrMatch = orTerms.some(t => termMatches(haystack, t, query.caseSensitive));
    if (!anyOrMatch) return false;
  }

  return true;
}

function termMatches(haystack: string, term: SearchTerm, caseSensitive: boolean): boolean {
  switch (term.type) {
    case 'include':
    case 'exclude':
    case 'exact': {
      const needle = caseSensitive ? term.value : term.value.toLowerCase();
      return haystack.includes(needle);
    }
    case 'regex': {
      if (!term.compiledRegex) return false; // invalid regex → no match
      return term.compiledRegex.test(caseSensitive ? haystack : haystack.toLowerCase());
    }
    default:
      return false;
  }
}

/* ── Highlighter ───────────────────────────────────────────────────────────── */

/**
 * Find all match positions in `text` for highlighting.
 * Excludes are intentionally skipped (don't highlight what we're excluding).
 */
export function getHighlightRanges(text: string, query: SearchQuery): HighlightRange[] {
  if (query.terms.length === 0) return [];

  const ranges: HighlightRange[] = [];
  const haystack = query.caseSensitive ? text : text.toLowerCase();

  for (const term of query.terms) {
    if (term.type === 'exclude') continue;

    if (term.type === 'regex') {
      if (!term.compiledRegex) continue;
      // Reset lastIndex for global matching
      const flags = query.caseSensitive ? 'g' : 'gi';
      const re = new RegExp(term.compiledRegex.source, flags);
      let m: RegExpExecArray | null;
      while ((m = re.exec(text)) !== null) {
        if (m[0].length === 0) { re.lastIndex++; continue; } // avoid infinite loop on zero-width
        ranges.push({ start: m.index, end: m.index + m[0].length });
      }
    } else {
      const needle = query.caseSensitive ? term.value : term.value.toLowerCase();
      let pos = 0;
      while (pos < haystack.length) {
        const idx = haystack.indexOf(needle, pos);
        if (idx === -1) break;
        ranges.push({ start: idx, end: idx + needle.length });
        pos = idx + 1;
      }
    }
  }

  // Sort and merge overlapping ranges
  if (ranges.length <= 1) return ranges;
  ranges.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: HighlightRange[] = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1];
    if (ranges[i].start <= last.end) {
      last.end = Math.max(last.end, ranges[i].end);
    } else {
      merged.push(ranges[i]);
    }
  }
  return merged;
}
